import React, { useMemo, useState } from 'react'
import { Button, Stack, TextField, Typography } from '@mui/material'
import { Spinner } from 'src/components/Spinner'
import { Check } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import {
  CoordinateMoveContext,
  DegreesMinutes,
  describeCoordinateMove,
  fromDegreesMinutes,
  measureMove,
  toDegreesMinutes,
} from './coordinates.action'
import { Translate } from './projectVersionChanges.action'

const MAX_LATITUDE = 90
const MAX_LONGITUDE = 180

export const COMPACT_BUTTON_SX = {
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 600,
  minHeight: 28,
  py: 0.25,
  px: 1,
  '& .MuiButton-startIcon > svg': { fontSize: 16 },
} as const

const FIELD_SX = {
  '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' },
  '& input': { py: 0.5, px: 0.5, fontSize: 13, textAlign: 'center', fontWeight: 600 },
} as const

interface CoordinateEditorProps {
  title: string
  latitude: number
  longitude: number
  saving: boolean
  // Resolves to whether the save worked.
  onSave: (latitude: number, longitude: number) => Promise<boolean>
  // Shown in the same row as the update button.
  actions?: React.ReactNode
  moveContext: Omit<CoordinateMoveContext, 'current'>
}

const hemisphere = (degrees: string, positive: string, negative: string) =>
  degrees.trim().startsWith('-') ? negative : positive

// Degrees and decimal minutes, the way coordinates are surveyed; remount with a new key when the position changes.
export const CoordinateEditor: React.FC<CoordinateEditorProps> = ({
  title,
  latitude,
  longitude,
  saving,
  onSave,
  actions,
  moveContext,
}) => {
  const { t: translate } = useTranslation(['projects'])
  const [lat, setLat] = useState<DegreesMinutes>(() => toDegreesMinutes(latitude))
  const [lon, setLon] = useState<DegreesMinutes>(() => toDegreesMinutes(longitude))
  // Stays set after a successful save; the editor is remounted once the saved coordinates come back.
  const [pending, setPending] = useState(false)
  const busy = saving || pending
  const save = async (next: { latitude: number; longitude: number }) => {
    setPending(true)
    if (!(await onSave(next.latitude, next.longitude))) setPending(false)
  }

  const next = useMemo(() => {
    const nextLat = fromDegreesMinutes(lat, MAX_LATITUDE)
    const nextLon = fromDegreesMinutes(lon, MAX_LONGITUDE)
    return nextLat == null || nextLon == null ? undefined : { latitude: nextLat, longitude: nextLon }
  }, [lat, lon])
  const move = next ? measureMove({ latitude, longitude }, next) : undefined
  const moved = !!move && move.meters >= 0.5

  const t: Translate = (key, options) => translate(key, { ns: 'projects', ...options }) as string
  const hint = describeCoordinateMove(next, { ...moveContext, current: { latitude, longitude } }, t)

  const field = (label: string, value: DegreesMinutes, set: (value: DegreesMinutes) => void, side: string) => (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography fontSize={13} sx={{ width: 36 }}>
        {label}
      </Typography>
      <TextField
        size="small"
        type="number"
        value={value.degrees}
        onChange={e => set({ ...value, degrees: e.target.value })}
        inputProps={{ 'aria-label': `${label} °`, step: 1 }}
        sx={{ ...FIELD_SX, width: 52 }}
      />
      <Typography fontSize={13}>°</Typography>
      <TextField
        size="small"
        type="number"
        value={value.minutes}
        onChange={e => set({ ...value, minutes: e.target.value })}
        inputProps={{ 'aria-label': `${label} ′`, step: 0.001 }}
        sx={{ ...FIELD_SX, width: 80 }}
      />
      <Typography fontSize={13} fontWeight={600}>{`′ ${side}`}</Typography>
    </Stack>
  )

  return (
    <Stack spacing={1}>
      <Typography fontSize={13} fontWeight={700}>
        {title}
      </Typography>
      {field(t('revision-lat'), lat, setLat, hemisphere(lat.degrees, 'N', 'S'))}
      {field(t('revision-long'), lon, setLon, hemisphere(lon.degrees, 'E', 'W'))}
      <Typography
        fontSize={12}
        color={hint.tone === 'error' ? 'error' : hint.tone === 'warning' ? 'warning.dark' : 'text.secondary'}
      >
        {hint.text}
      </Typography>
      <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          startIcon={busy ? <Spinner size={14} color="inherit" /> : <Check />}
          disabled={!next || busy || !moved}
          onClick={() => next && save(next)}
          sx={COMPACT_BUTTON_SX}
        >
          {t('revision-update-coordinates')}
        </Button>
        {actions}
      </Stack>
    </Stack>
  )
}

export default CoordinateEditor
